import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Select } from "@/components/Select";
import { Button } from "@/components/Button";
import { permissionsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

interface PermissionEditorModalProps {
    isOpen: boolean;
    itemKey: string;
    onClose: () => void;
}

type Option = { id: number; name: string; email?: string; tag?: string };

type PermissionRule = {
    user_id?: number | null;
    role_id?: number | null;
    tenant_id?: number | null;
};

export const PermissionEditorModal: React.FC<PermissionEditorModalProps> = ({
    isOpen,
    itemKey,
    onClose,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();
    const [users, setUsers] = useState<Option[]>([]);
    const [roles, setRoles] = useState<Option[]>([]);
    const [tenants, setTenants] = useState<Option[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
    const [selectedTenants, setSelectedTenants] = useState<number[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        Promise.all([permissionsApi.options(), permissionsApi.get(itemKey)])
            .then(([optionsResponse, permissionResponse]) => {
                const options = optionsResponse.data || optionsResponse;
                const rules = permissionResponse.data?.rules || [];
                setUsers(options.users || []);
                setRoles(options.roles || []);
                setTenants(options.tenants || []);
                setSelectedUsers([...new Set(rules.map((rule: PermissionRule) => rule.user_id).filter(Boolean))]);
                setSelectedRoles([...new Set(rules.map((rule: PermissionRule) => rule.role_id).filter(Boolean))]);
                setSelectedTenants([...new Set(rules.map((rule: PermissionRule) => rule.tenant_id).filter(Boolean))]);
            })
            .catch(() => showError(t("tools.permissions.load_error")));
    }, [isOpen, itemKey, showError, t]);

    const save = async (): Promise<void> => {
        setSaving(true);

        try {
            const users = selectedUsers.length ? selectedUsers : [null];
            const roles = selectedRoles.length ? selectedRoles : [null];
            const tenants = selectedTenants.length ? selectedTenants : [null];
            const rules: PermissionRule[] = [];

            users.forEach((user_id) => {
                roles.forEach((role_id) => {
                    tenants.forEach((tenant_id) => {
                        if (user_id || role_id || tenant_id) {
                            rules.push({ user_id, role_id, tenant_id });
                        }
                    });
                });
            });
            await permissionsApi.sync(itemKey, rules);
            showSuccess(t("tools.permissions.saved"));
            onClose();
        }
        catch {
            showError(t("tools.permissions.save_error"));
        }
        finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("tools.permissions.title")}
            footer={
                <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
                    <Button loading={saving} onClick={save}>{t("common.save")}</Button>
                </div>
            }
        >
            <div className="space-y-4">
                <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                    {t("tools.permissions.item_key")}: <strong>{itemKey}</strong>
                </div>
                <div className="space-y-4">
                    <Select<number> multiple searchable full value={selectedUsers} onChange={setSelectedUsers} options={users.map((user) => ({ value: user.id, label: user.email ? `${user.name} (${user.email})` : user.name }))} label={t("tools.permissions.users")} />
                    <Select<number> multiple searchable full value={selectedRoles} onChange={setSelectedRoles} options={roles.map((role) => ({ value: role.id, label: role.tag ? `${role.name} (${role.tag})` : role.name }))} label={t("tools.permissions.roles")} />
                    <Select<number> multiple searchable full value={selectedTenants} onChange={setSelectedTenants} options={tenants.map((tenant) => ({ value: tenant.id, label: tenant.name }))} label={t("tools.permissions.companies")} />
                </div>
            </div>
        </Modal>
    );
};
